import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatAddressLine, formatPhone } from './formatters';
import { displayFullName } from './teamDisplay';
import { assignmentHasEnded, assignmentsAtPlant, serviceLabel } from '../services/suppliers';

// Brand color used for the header bar and section headings in the PDF.
// Kept in sync with --brand-primary in theme.css.
const BRAND = [232, 119, 34]; // #E87722
const INK = [17, 24, 39]; // #111827
const MUTED = [100, 116, 139]; // #64748b
const DIVIDER = [229, 231, 235]; // #e5e7eb

const MARGIN_X = 48; // ~0.67"
const PAGE_TOP = 48;

// Path to the Proxemics logo inside the Vite `public/` folder. Vite
// serves public-folder assets from the site root, so this URL is
// stable in both dev and prod builds.
const LOGO_URL = '/Proxemics-Logo.png';

// Cache the logo load between PDF builds so repeated "Save as PDF"
// clicks don't refetch + redraw the PNG each time. The promise is
// reset on failure so a transient network hiccup doesn't permanently
// block the logo on this tab.
let logoPromise = null;
function loadLogo() {
  if (logoPromise) return logoPromise;
  logoPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Rasterise to a canvas so we can hand jsPDF a data URL it can
      // embed directly. Using the image's natural pixel size keeps the
      // logo crisp when scaled down to header size in the PDF.
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = (err) => {
      logoPromise = null;
      reject(err);
    };
    img.src = LOGO_URL;
  });
  return logoPromise;
}

// Use the same nickname-aware display as the Plant detail page so the
// names in the PDF match what users see on screen.
function fullName(m) {
  return displayFullName(m) || '';
}

function dashIfBlank(v) {
  const s = (v ?? '').toString().trim();
  return s || '—';
}

// Format a YYYY-MM-DD ISO date (the shape browsers send from <input
// type="date">) into a compact human-readable form for the PDF.
// Returns '' for blank values so dashIfBlank can paint a — in its
// place. Falls through to the raw string for legacy non-ISO values.
function formatVehicleIsoDate(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
  if (!m) return String(iso);
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Run a small layout engine that tracks the current Y cursor and auto-
// paginates on overflow. Centralising this keeps each section below
// simple — they just emit content and never think about page breaks.
function makeLayout(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - MARGIN_X * 2;
  const state = { y: PAGE_TOP };

  const ensureRoom = (needed) => {
    if (state.y + needed > pageHeight - 48) {
      doc.addPage();
      state.y = PAGE_TOP;
    }
  };

  return {
    pageWidth,
    pageHeight,
    usableWidth,
    get y() {
      return state.y;
    },
    advance(n) {
      state.y += n;
    },
    moveTo(n) {
      state.y = n;
    },
    ensureRoom,
  };
}

function drawHeader(doc, layout, plant, logo) {
  // Orange brand bar across the top.
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, layout.pageWidth, 6, 'F');

  // Proxemics logo at top-left. Scale to a fixed 45pt height and let
  // the natural aspect ratio determine the width so it never squishes.
  // When the logo fails to load we fall back to a text-only header so
  // the PDF still generates.
  let titleY = 34;
  if (logo) {
    const logoTop = 34; // generous breathing room below the orange bar
    const logoHeight = 45;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    doc.addImage(logo.dataUrl, 'PNG', MARGIN_X, logoTop, logoWidth, logoHeight);
    titleY = logoTop + logoHeight + 36; // generous gap above the plant name
  }

  // Plant name — big, left aligned.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...INK);
  doc.text(plant.Title || 'Plant', MARGIN_X, titleY);

  // Plant code / OEM subtitle in muted gray on the same line at right.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  const subtitle = [plant.PlantCode, plant.OEM].filter(Boolean).join(' · ');
  if (subtitle) {
    doc.text(subtitle, layout.pageWidth - MARGIN_X, titleY, { align: 'right' });
  }

  layout.moveTo(titleY + 24);
}

function sectionHeading(doc, layout, title) {
  layout.ensureRoom(30);
  layout.advance(8);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND);
  doc.text(title.toUpperCase(), MARGIN_X, layout.y);
  layout.advance(4);
  // Thin divider under the heading.
  doc.setDrawColor(...DIVIDER);
  doc.setLineWidth(0.5);
  doc.line(
    MARGIN_X,
    layout.y,
    layout.pageWidth - MARGIN_X,
    layout.y,
  );
  layout.advance(10);
}

// Render a label/value grid with a fixed number of columns. Each cell
// stacks its label (muted) above its value (dark). Rows wrap cleanly.
function labelValueGrid(doc, layout, cells, opts = {}) {
  const cols = opts.cols ?? 3;
  const colGap = 12;
  const rowGap = 14;
  const colWidth = (layout.usableWidth - colGap * (cols - 1)) / cols;

  for (let i = 0; i < cells.length; i += cols) {
    const rowCells = cells.slice(i, i + cols);
    // Estimate height for this row — max of any wrapped value.
    let rowHeight = 24;
    const wrapped = rowCells.map((c) => {
      const lines = doc.splitTextToSize(dashIfBlank(c.value), colWidth);
      rowHeight = Math.max(rowHeight, 10 + lines.length * 12 + 4);
      return lines;
    });
    layout.ensureRoom(rowHeight);
    rowCells.forEach((c, idx) => {
      const x = MARGIN_X + idx * (colWidth + colGap);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      doc.text((c.label || '').toUpperCase(), x, layout.y);
      doc.setFontSize(11);
      doc.setTextColor(...INK);
      doc.text(wrapped[idx], x, layout.y + 14);
    });
    layout.advance(rowHeight + rowGap - 10);
  }
}

function drawTable(doc, layout, head, body) {
  if (!body || body.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text('None.', MARGIN_X, layout.y);
    layout.advance(16);
    return;
  }
  autoTable(doc, {
    head: [head],
    body,
    startY: layout.y,
    margin: { left: MARGIN_X, right: MARGIN_X },
    styles: {
      font: 'helvetica',
      fontSize: 10,
      textColor: INK,
      cellPadding: 5,
      lineColor: DIVIDER,
      lineWidth: 0.5,
    },
    headStyles: {
      fillColor: [247, 248, 250], // #f7f8fa — neutral gray
      textColor: MUTED,
      fontStyle: 'bold',
      fontSize: 9,
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    theme: 'grid',
  });
  layout.moveTo(doc.lastAutoTable.finalY + 10);
}

/**
 * Build a multi-page PDF snapshot of a plant. `plantTeam` is the
 * roster-derived { coos, rms, lms, liaisons } shape computed on
 * PlantDetail — passing it in keeps this utility independent of the
 * team service. `options.parts` and `options.suppliersByCode` come
 * from the detail page's part/supplier lookup so the PDF can mirror
 * the Suppliers and Parts Received cards without refetching.
 * `options.directSuppliers` is the list of suppliers whose Plant
 * Locations & Coverage table names this plant directly — merged into
 * the Suppliers section so supplier-only (no parts yet) plants still
 * show up.
 */
export async function buildPlantPdf(
  plant,
  plantTeam = { coos: [], rms: [], lms: [], liaisons: [] },
  options = {},
) {
  const {
    parts = [],
    suppliersByCode = {},
    directSuppliers = [],
  } = options;
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const layout = makeLayout(doc);

  // Logo is best-effort: if the fetch fails for any reason we still
  // want the PDF to generate, just without the brand mark.
  let logo = null;
  try {
    logo = await loadLogo();
  } catch (err) {
    console.warn('[plantPdf] logo load failed, continuing without it', err);
  }

  drawHeader(doc, layout, plant, logo);

  // Plant Info ---------------------------------------------------------
  // Plant Code + OEM ride together on one row; the address renders
  // below them as a single full-width line — same treatment the
  // detail page uses so the PDF reads as a snapshot of the page.
  sectionHeading(doc, layout, 'Plant Info');
  labelValueGrid(
    doc,
    layout,
    [
      { label: 'Plant Code', value: plant.PlantCode },
      { label: 'OEM', value: plant.OEM },
    ],
    { cols: 2 },
  );
  labelValueGrid(
    doc,
    layout,
    [
      {
        label: 'Address',
        value: formatAddressLine({
          street: plant.AddressStreet || plant.Address,
          city: plant.City,
          state: plant.State,
          postalCode: plant.PostalCode,
          country: plant.Country,
        }),
      },
    ],
    { cols: 1 },
  );

  // Vehicles -----------------------------------------------------------
  sectionHeading(doc, layout, 'Vehicles');
  const vehicles = plant.vehicles || [];
  drawTable(
    doc,
    layout,
    ['Make', 'Model', 'Program', 'SOP Date', 'EOP Date', 'Status'],
    vehicles.map((v) => [
      dashIfBlank(v.make),
      dashIfBlank(v.model || v.name),
      // `program` was previously called `modelYear` — fall back so
      // PDFs of plants saved before the rename still show the value.
      dashIfBlank(v.program || v.modelYear),
      dashIfBlank(formatVehicleIsoDate(v.sopDate)),
      dashIfBlank(formatVehicleIsoDate(v.eopDate)),
      dashIfBlank(v.status),
    ]),
  );

  // Schedule Notes — free-form context attached to the vehicles
  // section (production-schedule slips, holiday shutdowns, ramp
  // plans). Only render when something's been written so the PDF
  // stays tight on plants without notes.
  if (plant.VehicleScheduleNotes) {
    layout.advance(6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text('SCHEDULE NOTES', MARGIN_X, layout.y);
    layout.advance(12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...INK);
    const lines = doc.splitTextToSize(
      plant.VehicleScheduleNotes,
      layout.usableWidth,
    );
    layout.ensureRoom(lines.length * 12 + 4);
    doc.text(lines, MARGIN_X, layout.y);
    layout.advance(lines.length * 12 + 6);
  }

  // Proxemics Team -----------------------------------------------------
  // Mirrors the "Proxemics Team at This Plant" card on the detail page:
  // COOs on top (C-suite tier), then Regional / Liaison Managers, with
  // individual Liaisons at the bottom.
  const teamHasAny =
    (plantTeam.coos?.length || 0) +
      (plantTeam.rms?.length || 0) +
      (plantTeam.lms?.length || 0) +
      (plantTeam.liaisons?.length || 0) >
    0;
  if (teamHasAny) {
    sectionHeading(doc, layout, 'Proxemics Team at This Plant');
    const cells = [];
    if (plantTeam.coos?.length) {
      cells.push({
        label: plantTeam.coos.length === 1 ? 'COO' : 'COOs',
        value: plantTeam.coos.map(fullName).filter(Boolean).join('\n'),
      });
    }
    if (plantTeam.rms?.length) {
      cells.push({
        label: plantTeam.rms.length === 1 ? 'Regional Manager' : 'Regional Managers',
        value: plantTeam.rms.map(fullName).filter(Boolean).join('\n'),
      });
    }
    if (plantTeam.lms?.length) {
      cells.push({
        label: plantTeam.lms.length === 1 ? 'Liaison Manager' : 'Liaison Managers',
        value: plantTeam.lms.map(fullName).filter(Boolean).join('\n'),
      });
    }
    if (plantTeam.liaisons?.length) {
      cells.push({
        label: 'Liaisons',
        value: plantTeam.liaisons.map(fullName).filter(Boolean).join('\n'),
      });
    }
    labelValueGrid(doc, layout, cells, { cols: 3 });
  }

  // Crew Shifts --------------------------------------------------------
  // The Plant form moved from a single ShiftsPerDay number plus a
  // schedule table to three day-of-week shift counts + a Shift Notes
  // textarea. Render the new fields when present; fall back to the
  // legacy data for plants saved before the migration.
  {
    const hasNewFields =
      plant.ShiftsWeekdays ||
      plant.ShiftsSaturdays ||
      plant.ShiftsSundays ||
      plant.ShiftNotes;
    const hasLegacyFields =
      plant.ShiftsPerDay || (plant.crewShifts || []).length;
    if (hasNewFields || hasLegacyFields) {
      sectionHeading(doc, layout, 'Crew Shifts');
      if (hasNewFields) {
        labelValueGrid(
          doc,
          layout,
          [
            {
              label: 'Shifts on Weekdays',
              value: String(plant.ShiftsWeekdays || '—'),
            },
            {
              label: 'Shifts on Saturdays',
              value: String(plant.ShiftsSaturdays || '—'),
            },
            {
              label: 'Shifts on Sundays',
              value: String(plant.ShiftsSundays || '—'),
            },
          ],
          { cols: 3 },
        );
        if (plant.ShiftNotes) {
          labelValueGrid(
            doc,
            layout,
            [{ label: 'Shift Notes', value: String(plant.ShiftNotes) }],
            { cols: 1 },
          );
        }
      } else {
        // Legacy fallback for plants whose data pre-dates the split.
        if (plant.ShiftsPerDay) {
          labelValueGrid(
            doc,
            layout,
            [{ label: 'Shift/s per Day', value: String(plant.ShiftsPerDay) }],
            { cols: 3 },
          );
        }
        if ((plant.crewShifts || []).length) {
          drawTable(
            doc,
            layout,
            ['Shift', 'Schedule'],
            plant.crewShifts.map((s) => [
              dashIfBlank(s.shift),
              dashIfBlank(s.schedule),
            ]),
          );
        }
      }
    }
  }

  // Active supplier stat tiles ----------------------------------------
  // Mirrors the two stat boxes that sit above the Suppliers card on
  // the detail page. Computed inline from the same inputs the
  // Suppliers section uses (parts + directSuppliers + suppliersByCode
  // + the plant's shifts-per-day) so the PDF reads as a faithful
  // snapshot. See the activeSupplierStats memo in PlantDetail.jsx
  // for the source of these conversion rules.
  {
    const partsCodes = (parts || []).map((pt) => pt.SupplierCode).filter(Boolean);
    const directCodes = (directSuppliers || [])
      .map((s) => s.SupplierCode)
      .filter(Boolean);
    const mergedCodes = Array.from(new Set([...partsCodes, ...directCodes]));
    const lookup = { ...(suppliersByCode || {}) };
    for (const s of directSuppliers || []) {
      if (s.SupplierCode && !lookup[s.SupplierCode]) lookup[s.SupplierCode] = s;
    }
    // Days per week the plant runs — weekdays (5) plus each weekend day
    // the plant has shifts. The plant's schedule is the single source of
    // truth for coverage days (per-supplier coverageDays was retired).
    const plantDaysPerWeek =
      5 +
      (Number(plant?.ShiftsSaturdays) > 0 ? 1 : 0) +
      (Number(plant?.ShiftsSundays) > 0 ? 1 : 0);
    const shiftsPerDay = (() => {
      // Same priority chain PlantDetail uses: new weekday count first,
      // then legacy ShiftsPerDay, then crewShifts row count, then 1.
      const weekday = Number(plant?.ShiftsWeekdays);
      if (Number.isFinite(weekday) && weekday > 0) return weekday;
      const legacy = Number(plant?.ShiftsPerDay);
      if (Number.isFinite(legacy) && legacy > 0) return legacy;
      const fromCrew = Array.isArray(plant?.crewShifts)
        ? plant.crewShifts.length
        : 0;
      return fromCrew > 0 ? fromCrew : 1;
    })();
    const plantCodeUpper = String(plant?.PlantCode || '').toUpperCase();
    let activeCount = 0;
    let activeHoursPerDay = 0;
    for (const code of mergedCodes) {
      const supplier = lookup[code];
      // Every Active service row at this plant contributes hours; the
      // supplier is counted once (mirrors PlantDetail).
      const active = assignmentsAtPlant(supplier, plantCodeUpper).filter(
        (a) => (a.coverageStatus || '') === 'Active' && !assignmentHasEnded(a),
      );
      if (active.length === 0) continue;
      activeCount += 1;
      for (const a of active) {
      const hrs = Number(a.hoursAllocated);
      if (!Number.isFinite(hrs) || hrs <= 0) continue;
      const days = plantDaysPerWeek;
      const unit = String(a.hoursUnit || '').toLowerCase();
      if (unit === 'per shift') activeHoursPerDay += hrs * shiftsPerDay;
      else if (unit === 'per week') activeHoursPerDay += hrs / days;
      else if (unit === 'per month') activeHoursPerDay += hrs / (days * 4.33);
      else activeHoursPerDay += hrs; // Per Day / Other → literal
      }
    }
    // Draw two tile boxes side-by-side. Each tile: rounded rectangle
    // outline, big orange number top, muted uppercase label below.
    // Box height of 48pt keeps the tile compact alongside a regular
    // section heading; gap of 12pt matches the on-screen 16px gap
    // visually at PDF scale.
    const tileHeight = 48;
    const tileGap = 12;
    const tileWidth = (layout.usableWidth - tileGap) / 2;
    layout.ensureRoom(tileHeight + 16);
    const tileTop = layout.y;
    const drawTile = (xOffset, value, label) => {
      const x = MARGIN_X + xOffset;
      doc.setDrawColor(229, 231, 235); // var(--brand-border) #e5e7eb
      doc.setFillColor(255, 255, 255);
      // jsPDF supports rounded rects via roundedRect(x, y, w, h, rx, ry, style)
      doc.roundedRect(x, tileTop, tileWidth, tileHeight, 6, 6, 'FD');
      // Big orange value, top-left
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(...BRAND);
      doc.text(String(value), x + 14, tileTop + 22);
      // Muted uppercase label below the value
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      doc.text(label.toUpperCase(), x + 14, tileTop + 38);
    };
    drawTile(0, activeCount, 'Active Suppliers');
    drawTile(tileWidth + tileGap, Number(activeHoursPerDay.toFixed(1)), 'Active Hours / Day');
    layout.advance(tileHeight + 16);
  }

  // Suppliers ----------------------------------------------------------
  // Mirrors the merged supplier table on the detail page — suppliers
  // whose parts ship to this plant PLUS suppliers whose Plant
  // Locations & Coverage entry names this plant directly. Each row
  // shows the same six columns the on-screen table does so the PDF
  // is a faithful snapshot of what's on the page.
  {
    sectionHeading(doc, layout, 'Suppliers');
    const partsCodes = (parts || []).map((pt) => pt.SupplierCode).filter(Boolean);
    const directCodes = (directSuppliers || [])
      .map((s) => s.SupplierCode)
      .filter(Boolean);
    const supplierCodes = Array.from(new Set([...partsCodes, ...directCodes]));
    // Prefer the parts-derived suppliersByCode lookup but fall back to
    // the direct-supplier records so names resolve for supplier-only
    // entries too.
    const lookup = { ...(suppliersByCode || {}) };
    for (const s of directSuppliers || []) {
      if (s.SupplierCode && !lookup[s.SupplierCode]) {
        lookup[s.SupplierCode] = s;
      }
    }
    if (supplierCodes.length === 0) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(...MUTED);
      doc.text('None linked yet.', MARGIN_X, layout.y);
      layout.advance(16);
    } else {
      const upperPlantCode = String(plant.PlantCode || '').toUpperCase();
      // Build sortable rows up front so the table is alphabetical by
      // supplier name (falling back to code for unknowns), matching
      // the detail page's sort.
      const rows = supplierCodes
        .flatMap((code) => {
          // One row per (supplier, service) — mirrors PlantDetail.
          const supplier = lookup[code];
          const atPlant = assignmentsAtPlant(supplier, upperPlantCode);
          return (atPlant.length ? atPlant : [null]).map((assignment) => ({
            code,
            supplier,
            assignment,
          }));
        })
        .map(({ code, supplier, assignment }) => {
          const supplierParts = (parts || [])
            .filter(
              (pt) =>
                String(pt.SupplierCode || '').toUpperCase() ===
                String(code).toUpperCase(),
            )
            .sort((a, b) => (a.Title || '').localeCompare(b.Title || ''));
          return {
            code,
            name: supplier?.Title || null,
            assignment,
            parts: supplierParts,
          };
        })
        .sort((a, b) => {
          // Alphabetical by supplier name — same rules as the
          // detail page so the PDF reads as a faithful snapshot.
          // Named rows first (case + accent insensitive, numeric
          // runs treated as numbers); unnamed orphan codes sink
          // to the bottom in code order.
          const aHasName = !!(a.name && a.name.trim());
          const bHasName = !!(b.name && b.name.trim());
          if (aHasName && !bHasName) return -1;
          if (!aHasName && bHasName) return 1;
          if (aHasName && bHasName) {
            return a.name.localeCompare(b.name, undefined, {
              sensitivity: 'base',
              numeric: true,
            });
          }
          return String(a.code).localeCompare(String(b.code), undefined, {
            numeric: true,
          });
        });

      drawTable(
        doc,
        layout,
        ['Supplier', 'Part/s', 'Rate', 'Coverage Hours', 'Coverage Unit', 'Status'],
        rows.map((r) => {
          const a = r.assignment;
          // Account number leads, then the supplier name — same order
          // the on-screen table uses so the PDF reads as a faithful
          // snapshot.
          const service = a ? serviceLabel(a) : '';
          const supplierLabel =
            (r.name
              ? `${r.code}  ${r.name}`
              : `${r.code} (not in Suppliers list)`) +
            (service ? `\n${service}` : '');
          const partsLabel =
            r.parts.length === 0
              ? '—'
              : r.parts
                  .map((pt) => pt.Title || pt.PartCode || '—')
                  .join(', ');
          const rate =
            a && a.hourlyRate != null && a.hourlyRate !== ''
              ? `$${Number(a.hourlyRate).toFixed(2)}/hr`
              : '—';
          const hours =
            a && a.hoursAllocated != null && a.hoursAllocated !== ''
              ? String(Number(a.hoursAllocated))
              : '—';
          const unit = (a && a.hoursUnit) || '—';
          const status =
            ((a && a.coverageStatus) || '—') + (a && assignmentHasEnded(a) ? ' (Ended)' : '');
          return [
            supplierLabel,
            partsLabel,
            rate,
            hours,
            unit,
            status,
          ];
        }),
      );
    }
  }

  // Parts Received -----------------------------------------------------
  // One block per part: a header line with the part's supplier + the
  // vehicles it ships to at this plant, an optional description, and a
  // Plant-Side Locations sub-table that mirrors PartDetail.jsx (Part
  // Type / Description / Part # / Sequenced / Installed / Kitting). The
  // goal is a printable "everything about every part at this plant"
  // record, so anyone on site can read one page and see what they're
  // receiving, where it ends up, and where it's stored.
  {
    sectionHeading(doc, layout, 'Parts Received');
    const rows = parts || [];
    if (rows.length === 0) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(...MUTED);
      doc.text('None.', MARGIN_X, layout.y);
      layout.advance(16);
    } else {
      rows.forEach((p, idx) => {
        const vehiclesForPlant = (p.plantVehicles || [])
          .filter((pv) => pv.plantCode === plant.PlantCode)
          .map((pv) => pv.vehicle)
          .filter(Boolean);
        // Account number leads, then the supplier display name —
        // mirrors the Suppliers table treatment above. If the part
        // points at a code not on the Suppliers list, just show the
        // code on its own.
        const supplierTitle = p.SupplierCode
          ? suppliersByCode?.[p.SupplierCode]?.Title || ''
          : '';
        const supplierLabel = p.SupplierCode
          ? supplierTitle
            ? `${p.SupplierCode}  ${supplierTitle}`
            : p.SupplierCode
          : '—';

        // Per-part header: bold part name on the left, supplier on the
        // right. Kept as inline draws (not a table) so each part feels
        // like its own record rather than a row in a grid.
        layout.ensureRoom(22);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...INK);
        doc.text(p.Title || 'Untitled Part', MARGIN_X, layout.y);
        if (p.SupplierCode) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(...MUTED);
          doc.text(supplierLabel, layout.pageWidth - MARGIN_X, layout.y, {
            align: 'right',
          });
        }
        layout.advance(14);

        // Meta row: Part # + Vehicles — rendered as muted key/value pairs
        // so the scan-down-the-page flow stays easy to read.
        const metaBits = [];
        if (p.PartNumber) metaBits.push(`Part #: ${p.PartNumber}`);
        metaBits.push(
          `Vehicles: ${
            vehiclesForPlant.length
              ? vehiclesForPlant.join(', ')
              : 'All / unspecified'
          }`,
        );
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...MUTED);
        doc.text(metaBits.join('   ·   '), MARGIN_X, layout.y);
        layout.advance(12);

        // Optional description, wrapped to the usable width.
        if (p.Description && p.Description.trim()) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(...INK);
          const lines = doc.splitTextToSize(p.Description.trim(), layout.usableWidth);
          layout.ensureRoom(lines.length * 12 + 4);
          doc.text(lines, MARGIN_X, layout.y);
          layout.advance(lines.length * 12 + 4);
        }

        // Plant-Side Locations sub-table. Columns match the on-screen
        // PartDetail table exactly so someone flipping between the PDF
        // and the portal sees the same thing.
        const variantRows = (p.variants || []).map((v) => [
          dashIfBlank(v.variantName),
          dashIfBlank(v.description),
          dashIfBlank(v.partNumber),
          dashIfBlank(v.sequencingLocation),
          dashIfBlank(v.assemblyLocation),
          dashIfBlank(v.warehouseLocation),
        ]);
        if (variantRows.length === 0) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(9);
          doc.setTextColor(...MUTED);
          layout.ensureRoom(14);
          doc.text(
            'No plant-side locations recorded.',
            MARGIN_X,
            layout.y,
          );
          layout.advance(14);
        } else {
          drawTable(
            doc,
            layout,
            [
              'Part Type',
              'Description',
              'Part #',
              'Sequenced',
              'Installed',
              'Kitting / Storage',
            ],
            variantRows,
          );
        }

        // Thin divider between parts (skip after the last one).
        if (idx < rows.length - 1) {
          layout.advance(6);
          doc.setDrawColor(...DIVIDER);
          doc.setLineWidth(0.5);
          doc.line(
            MARGIN_X,
            layout.y,
            layout.pageWidth - MARGIN_X,
            layout.y,
          );
          layout.advance(12);
        }
      });
    }
  }

  // Safety Equipment (PPE) --------------------------------------------
  const ppe = plant.ppe || {};
  const ppeHasAny =
    ppe.vestColor ||
    ppe.safetyGlasses ||
    ppe.safetyShoes ||
    ppe.sleeves ||
    ppe.gloves ||
    ppe.scratchGuards;
  if (ppeHasAny) {
    sectionHeading(doc, layout, 'Safety Equipment (PPE)');
    labelValueGrid(
      doc,
      layout,
      [
        { label: 'Vest Color', value: ppe.vestColor },
        { label: 'Safety Glasses', value: ppe.safetyGlasses },
        { label: 'Safety Shoes', value: ppe.safetyShoes },
        { label: 'Sleeves', value: ppe.sleeves },
        { label: 'Gloves', value: ppe.gloves },
        { label: 'Scratch Guards / ISD Protection', value: ppe.scratchGuards },
      ],
      { cols: 3 },
    );
  }

  // Plant Closures -----------------------------------------------------
  if ((plant.plantClosures || []).length) {
    sectionHeading(doc, layout, 'Plant Closures');
    drawTable(
      doc,
      layout,
      ['Start Date', 'End Date', 'Reason'],
      plant.plantClosures.map((c) => [
        dashIfBlank(c.startDate),
        dashIfBlank(c.endDate),
        dashIfBlank(c.reason),
      ]),
    );
  }

  // OEM Plant Contacts -------------------------------------------------
  // Plant Manager used to be a dedicated set of fields above this
  // list; it's now just one of the contacts (with title="Plant
  // Manager"), so the section is a single table of all contacts.
  // Iterates plantContacts in saved array order — the form's up/down
  // reorder buttons persist that order on the supplier record, and
  // we deliberately do not sort here so the PDF mirrors what the
  // user arranged on the detail page.
  if ((plant.plantContacts || []).length > 0) {
    sectionHeading(doc, layout, 'OEM Plant Contacts');
    drawTable(
      doc,
      layout,
      ['Name', 'Company', 'Title', 'Email', 'Cell Phone', 'Office Phone'],
      plant.plantContacts.map((c) => [
        dashIfBlank(c.name),
        dashIfBlank(c.company),
        dashIfBlank(c.title),
        dashIfBlank(c.email),
        dashIfBlank(formatPhone(c.phone)),
        dashIfBlank(formatPhone(c.officePhone)),
      ]),
    );
  }

  // Sequencing Facilities ----------------------------------------------
  // Plants can have multiple sequencing facilities. Each facility renders
  // as its own labeled block under one "Sequencing Facilities" heading.
  const facilities = Array.isArray(plant.sequencingFacilities)
    ? plant.sequencingFacilities.filter(
        (f) => f && (f.name || f.address || f.notes),
      )
    : [];
  if (facilities.length > 0) {
    sectionHeading(doc, layout, 'Sequencing Facilities');
    facilities.forEach((f, i) => {
      labelValueGrid(
        doc,
        layout,
        [
          { label: `Facility ${i + 1} Name`, value: f.name },
          { label: 'Address', value: f.address },
          { label: 'Notes', value: f.notes },
        ],
        { cols: 3 },
      );
    });
  }

  // Notes --------------------------------------------------------------
  if (plant.Notes) {
    sectionHeading(doc, layout, 'Notes');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...INK);
    const lines = doc.splitTextToSize(plant.Notes, layout.usableWidth);
    layout.ensureRoom(lines.length * 12 + 4);
    doc.text(lines, MARGIN_X, layout.y);
    layout.advance(lines.length * 12 + 4);
  }

  // Footer on every page: generated date + page number in muted gray.
  const pageCount = doc.internal.getNumberOfPages();
  const generated = new Date().toLocaleDateString();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(
      `Generated ${generated}`,
      MARGIN_X,
      doc.internal.pageSize.getHeight() - 24,
    );
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() - MARGIN_X,
      doc.internal.pageSize.getHeight() - 24,
      { align: 'right' },
    );
  }

  return doc;
}

/**
 * Build and download the plant PDF with a filename based on the plant's
 * code or name. Falls back to a generic filename if neither is set.
 * `options` forwards `parts` and `suppliersByCode` through to
 * buildPlantPdf so the Suppliers and Parts Received sections render.
 */
export async function savePlantPdf(plant, plantTeam, options = {}) {
  const doc = await buildPlantPdf(plant, plantTeam, options);
  // Filename pattern: ProxemicsConsulting-PlantBook-{PlantCode}.pdf.
  // Prefer the plant code (short, unambiguous like "SHAP"); fall back
  // to a slug of the plant name for records that somehow have no code.
  const identifier =
    plant.PlantCode ||
    (plant.Title || 'Plant')
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/(^-|-$)/g, '') ||
    'Plant';
  doc.save(`ProxemicsConsulting-PlantBook-${identifier}.pdf`);
}
